import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import * as identity from '@repletoryreach/sdk-api/identity';
import {
  setTokens,
  clearTokens,
  readAccessToken,
  readRefreshToken,
  readImpersonationToken,
  setImpersonationToken,
  clearImpersonationToken,
  setRefreshHandler,
  configure,
  getConfig
} from '@repletoryreach/sdk-api';
import { isJwtExpired, getJwtExpiry } from '@repletoryreach/sdk-utils';
import { useAppStore, bootstrapFromJwt } from '@repletoryreach/sdk-utils/store';
import { refreshAccessToken } from './refresh.js';

export type AuthDomain = 'tenant' | 'platform';

/**
 * Thrown by `login` when the account has 2FA enabled. The caller should
 * collect a TOTP/recovery code and call `completeTwoFactor(code)`. The
 * pending challenge token is held inside the provider: callers never see it.
 */
export class TwoFactorRequiredError extends Error {
  constructor() {
    super('Two-factor authentication required.');
    this.name = 'TwoFactorRequiredError';
  }
}

/**
 * Thrown by `login` when the account must set up two-factor sign-in before its first session
 * (platform staff invited from the console, 3 October 2026). The caller shows the secret from
 * `startTwoFactorSetup()`, takes a code with `confirmTwoFactorSetup(code)` (which returns the
 * recovery codes), and calls `finishTwoFactorSetup()` once they are saved.
 */
export class TwoFactorSetupRequiredError extends Error {
  constructor() {
    super('Two-factor sign-in has to be set up first.');
    this.name = 'TwoFactorSetupRequiredError';
  }
}

export interface AuthUser {
  id: string;
  email: string;
  display_name?: string | null;
  locale: string;
  /**
   * Sign-in domain claim. 'tenant' for normal org users, 'platform' for
   * RepletoryReach staff. Mirrors the `scope` claim on the JWT.
   */
  scope: AuthDomain;
  /** Whether the user's email address has been verified. */
  email_verified: boolean;
  /** Whether TOTP two-factor is enabled on the account. */
  two_factor_enabled: boolean;
  org: identity.Organisation;
  /** W31: the impersonating platform admin's email when this is an impersonation session. */
  impersonated_by?: string | null;
  /** owner, admin or agent in the organisation (2 October 2026); null for platform staff. */
  role?: identity.OrgRole | null;
}

export interface AuthState {
  user: AuthUser | null;
  isAuthenticated: boolean;
  loading: boolean;
  /** Logs in. Throws `TwoFactorRequiredError` when the account has 2FA on. */
  login: (email: string, password: string) => Promise<void>;
  /** Completes a 2FA challenge started by `login`. */
  completeTwoFactor: (code: string) => Promise<void>;
  /** True while a sign-in is waiting for two-factor to be set up (`TwoFactorSetupRequiredError`). */
  hasPendingTwoFactorSetup: () => boolean;
  /** The authenticator secret for the setup `login` asked for. */
  startTwoFactorSetup: () => Promise<{ secret: string; otpauth_uri: string }>;
  /** The first code: switches two-factor on and returns the recovery codes. The session waits for `finishTwoFactorSetup`. */
  confirmTwoFactorSetup: (code: string) => Promise<string[]>;
  /** Signs in with the session `confirmTwoFactorSetup` received. */
  finishTwoFactorSetup: () => Promise<void>;
  /**
   * Finishes a sign-in that went through Google, Facebook or Apple.
   *
   * `code` is the single-use handoff the provider callback left in the
   * console's address bar. Identity has already spent the provider's
   * authorisation code and verified its identity token server-side; this
   * swaps the handoff for the ordinary session, so nothing downstream can
   * tell how the person signed in. Like `login`, it throws
   * `TwoFactorRequiredError` when the account has an authenticator (finish with
   * `completeTwoFactor`), and `TwoFactorSetupRequiredError` when it must set
   * one up first (9 October 2026).
   */
  completeSocialSignIn: (code: string, provider?: string) => Promise<void>;
  signup: (
    email: string, password: string, orgName: string, countryIso?: string,
    referral?: { code: string; source?: 'link' | 'code_at_signup' }
  ) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
  forgotPassword: (email: string) => Promise<void>;
  resetPassword: (email: string, token: string, newPassword: string) => Promise<void>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>;
  verifyEmail: (email: string, code: string) => Promise<void>;
  resendVerification: (email: string) => Promise<void>;
  /** W31: true when the current session is a platform-admin impersonation token. */
  isImpersonating: boolean;
  /** W31: the impersonating admin's email, when impersonating. */
  impersonatedBy: string | null;
  /** W31: adopt a freshly minted impersonation token (tab-scoped; no refresh). */
  beginImpersonation: (token: string) => Promise<void>;
  /** W31: drop the impersonation session; falls back to any persisted real session. */
  endImpersonation: () => Promise<void>;
}

const AuthCtx = createContext<AuthState | null>(null);

export interface AuthProviderProps {
  children: ReactNode;
  /**
   * Sign-in domain for this app. The tenant console must pass `'tenant'`
   * and the admin console must pass `'platform'`. The identity service
   * refuses cross-domain login attempts with HTTP 403. Defaults to
   * `'tenant'` so existing tenant apps keep working.
   */
  domain?: AuthDomain;
  onAuthenticated?: (user: AuthUser) => void;
  onSignedOut?: () => void;
  onAuthExpired?: () => void;
}

export function AuthProvider({
  children,
  domain = 'tenant',
  onAuthenticated,
  onSignedOut,
  onAuthExpired
}: AuthProviderProps) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  const refreshTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Holds the short-lived challenge token between `login` (which throws
  // TwoFactorRequiredError) and `completeTwoFactor`.
  const pendingTwoFactor = useRef<string | null>(null);
  // The setup token between `login` (TwoFactorSetupRequiredError) and `confirmTwoFactorSetup`, then the
  // session it returned, held until the recovery codes have been shown.
  const pendingSetup = useRef<string | null>(null);
  const setupSession = useRef<{ token: string; refresh_token: string } | null>(null);

  const clearRefreshTimer = useCallback(() => {
    if (refreshTimer.current !== null) {
      clearTimeout(refreshTimer.current);
      refreshTimer.current = null;
    }
  }, []);

  const scheduleRefresh = useCallback((token: string) => {
    clearRefreshTimer();
    const expiry = getJwtExpiry(token);
    if (!expiry) return;
    const leewayMs = getConfig().auth.autoRefreshLeewaySec * 1000;
    const delay = Math.max(0, expiry.getTime() - Date.now() - leewayMs);
    refreshTimer.current = setTimeout(() => {
      void refreshAccessToken().then((next) => {
        if (next) scheduleRefresh(next);
      });
    }, delay);
  }, [clearRefreshTimer]);

  const hydrateUser = useCallback(async () => {
    const res = await identity.me();
    const scope: AuthDomain = res.data.scope === 'platform' ? 'platform' : 'tenant';
    const u: AuthUser = {
      id: res.data.id,
      email: res.data.email,
      display_name: res.data.display_name ?? null,
      locale: res.data.locale,
      scope,
      email_verified: res.data.email_verified ?? false,
      two_factor_enabled: res.data.two_factor_enabled ?? false,
      org: res.data.org,
      impersonated_by: res.data.impersonated_by ?? null,
      role: res.data.role ?? null
    };
    setUser(u);
    useAppStore.getState().setUser({
      id: u.id,
      email: u.email,
      organisationId: u.org.id
    });
    configure({ defaults: { timezone: u.org.timezone, locale: u.locale, countryIso: u.org.country_iso, currency: getConfig().defaults.currency } });
    return u;
  }, []);

  // Register refresh handler + onAuthExpired + boot rehydrate
  useEffect(() => {
    setRefreshHandler(refreshAccessToken);
    if (onAuthExpired) {
      configure({ onAuthExpired: () => { setUser(null); clearRefreshTimer(); onAuthExpired(); } });
    }
    return () => setRefreshHandler(null);
  }, [onAuthExpired, clearRefreshTimer]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      // W31: an impersonation token (tab-scoped) takes precedence. Expired
      // ones are dropped so the tab falls back to any persisted real session.
      const imp = readImpersonationToken();
      if (imp && isJwtExpired(imp)) clearImpersonationToken();

      const token = readAccessToken();
      if (!token) {
        setLoading(false);
        return;
      }
      if (isJwtExpired(token)) {
        const refreshed = await refreshAccessToken();
        if (!refreshed) {
          if (!cancelled) setLoading(false);
          return;
        }
      }
      // A page load restores the session from storage without going through setTokens, so the in-memory
      // copy stayed empty until the first renewal, and live updates (which start from it) stayed off for
      // most of the token's life. Load it now. Not setTokens: an impersonation token must stay tab-scoped.
      const restored = readAccessToken();
      if (restored && useAppStore.getState().accessToken !== restored) bootstrapFromJwt(restored);
      try {
        await hydrateUser();
        // Impersonation sessions never schedule refresh: they just expire.
        if (!readImpersonationToken()) {
          const current = readAccessToken();
          if (current) scheduleRefresh(current);
        }
      } catch {
        if (readImpersonationToken()) clearImpersonationToken();
        else clearTokens();
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
      clearRefreshTimer();
    };
  }, [hydrateUser, scheduleRefresh, clearRefreshTimer]);

  const login = useCallback(async (email: string, password: string) => {
    const res = await identity.login({ email, password, domain });
    if (identity.isTwoFactorSetupChallenge(res.data)) {
      pendingSetup.current = res.data.setup_token;
      throw new TwoFactorSetupRequiredError();
    }
    if (identity.isTwoFactorChallenge(res.data)) {
      pendingTwoFactor.current = res.data.pending_token;
      throw new TwoFactorRequiredError();
    }
    setTokens(res.data.token, res.data.refresh_token);
    const u = await hydrateUser();
    scheduleRefresh(res.data.token);
    onAuthenticated?.(u);
  }, [domain, hydrateUser, scheduleRefresh, onAuthenticated]);

  const completeTwoFactor = useCallback(async (code: string) => {
    const pending = pendingTwoFactor.current;
    if (!pending) throw new Error('No pending two-factor challenge.');
    const res = await identity.loginTwoFactor(pending, code);
    pendingTwoFactor.current = null;
    setTokens(res.data.token, res.data.refresh_token);
    const u = await hydrateUser();
    scheduleRefresh(res.data.token);
    onAuthenticated?.(u);
  }, [hydrateUser, scheduleRefresh, onAuthenticated]);

  const hasPendingTwoFactorSetup = useCallback(() => pendingSetup.current !== null || setupSession.current !== null, []);

  const startTwoFactorSetup = useCallback(async () => {
    const token = pendingSetup.current;
    if (!token) throw new Error('No two-factor setup is waiting.');
    return (await identity.loginTwoFactorSetup(token)).data;
  }, []);

  const confirmTwoFactorSetup = useCallback(async (code: string) => {
    const token = pendingSetup.current;
    if (!token) throw new Error('No two-factor setup is waiting.');
    const res = await identity.confirmLoginTwoFactorSetup(token, code);
    pendingSetup.current = null;
    setupSession.current = { token: res.data.token, refresh_token: res.data.refresh_token };
    return res.data.recovery_codes ?? [];
  }, []);

  const finishTwoFactorSetup = useCallback(async () => {
    const session = setupSession.current;
    if (!session) throw new Error('No session is waiting.');
    setupSession.current = null;
    setTokens(session.token, session.refresh_token);
    const u = await hydrateUser();
    scheduleRefresh(session.token);
    onAuthenticated?.(u);
  }, [hydrateUser, scheduleRefresh, onAuthenticated]);

  const completeSocialSignIn = useCallback(async (code: string, provider?: string) => {
    const res = await identity.exchangeSocialCode(code, provider);
    if (identity.isTwoFactorSetupChallenge(res.data)) {
      pendingSetup.current = res.data.setup_token;
      throw new TwoFactorSetupRequiredError();
    }
    if (identity.isTwoFactorChallenge(res.data)) {
      pendingTwoFactor.current = res.data.pending_token;
      throw new TwoFactorRequiredError();
    }
    setTokens(res.data.token, res.data.refresh_token);
    const u = await hydrateUser();
    scheduleRefresh(res.data.token);
    onAuthenticated?.(u);
  }, [hydrateUser, scheduleRefresh, onAuthenticated]);

  const forgotPassword = useCallback(async (email: string) => {
    await identity.forgotPassword(email);
  }, []);

  const resetPassword = useCallback(async (email: string, token: string, newPassword: string) => {
    await identity.resetPassword(email, token, newPassword);
  }, []);

  const changePassword = useCallback(async (currentPassword: string, newPassword: string) => {
    await identity.changePassword(currentPassword, newPassword);
  }, []);

  const verifyEmail = useCallback(async (email: string, code: string) => {
    await identity.verifyEmail(email, code);
    // Re-hydrate so `user.email_verified` flips for the soft-gate banner.
    if (readAccessToken()) await hydrateUser();
  }, [hydrateUser]);

  const resendVerification = useCallback(async (email: string) => {
    await identity.resendVerification(email);
  }, []);

  const signup = useCallback(async (
    email: string, password: string, orgName: string, countryIso?: string,
    referral?: { code: string; source?: 'link' | 'code_at_signup' }
  ) => {
    const req: identity.SignupRequest = { email, password, org_name: orgName };
    if (countryIso !== undefined) req.country_iso = countryIso;
    // W47: a partner's code, and whether it was typed or came in on a link.
    // A code nobody holds is dropped server-side rather than refused, so a
    // mistyped one never costs somebody their account.
    if (referral?.code) {
      req.referral_code = referral.code;
      req.referral_source = referral.source ?? 'code_at_signup';
    }
    const res = await identity.signup(req);
    setTokens(res.data.token, res.data.refresh_token);
    const u = await hydrateUser();
    scheduleRefresh(res.data.token);
    onAuthenticated?.(u);
  }, [hydrateUser, scheduleRefresh, onAuthenticated]);

  const logout = useCallback(async () => {
    try {
      await identity.logout(readRefreshToken() ?? undefined);
    } catch {
      // best-effort
    }
    clearTokens();
    setUser(null);
    clearRefreshTimer();
    onSignedOut?.();
  }, [clearRefreshTimer, onSignedOut]);

  const refresh = useCallback(async () => {
    // W31: impersonation sessions never refresh; just re-hydrate.
    if (readImpersonationToken()) {
      await hydrateUser();
      return;
    }
    const next = await refreshAccessToken();
    if (next) {
      scheduleRefresh(next);
      await hydrateUser();
    } else {
      setUser(null);
      clearRefreshTimer();
    }
  }, [scheduleRefresh, hydrateUser, clearRefreshTimer]);

  // ── W31: impersonation ──────────────────────────────────────────────
  const beginImpersonation = useCallback(async (token: string) => {
    setImpersonationToken(token);
    clearRefreshTimer();           // no auto-refresh while impersonating
    try {
      await hydrateUser();
    } catch (err) {
      clearImpersonationToken();
      throw err;
    }
  }, [hydrateUser, clearRefreshTimer]);

  const endImpersonation = useCallback(async () => {
    clearImpersonationToken();
    setUser(null);
    clearRefreshTimer();
    // Fall back to a persisted real session in this browser, if any.
    const token = readAccessToken();
    if (token && !isJwtExpired(token)) {
      try {
        await hydrateUser();
        scheduleRefresh(token);
        return;
      } catch { /* fall through to signed-out */ }
    }
    onSignedOut?.();
  }, [hydrateUser, scheduleRefresh, clearRefreshTimer, onSignedOut]);

  const value = useMemo<AuthState>(() => ({
    user,
    isAuthenticated: user !== null,
    loading,
    login,
    completeTwoFactor,
    hasPendingTwoFactorSetup,
    startTwoFactorSetup,
    confirmTwoFactorSetup,
    finishTwoFactorSetup,
    completeSocialSignIn,
    signup,
    logout,
    refresh,
    forgotPassword,
    resetPassword,
    changePassword,
    verifyEmail,
    resendVerification,
    isImpersonating: Boolean(user?.impersonated_by),
    impersonatedBy: user?.impersonated_by ?? null,
    beginImpersonation,
    endImpersonation
  }), [
    user, loading, login, completeTwoFactor, hasPendingTwoFactorSetup, startTwoFactorSetup, confirmTwoFactorSetup,
    finishTwoFactorSetup, completeSocialSignIn, signup, logout, refresh,
    forgotPassword, resetPassword, changePassword, verifyEmail, resendVerification,
    beginImpersonation, endImpersonation
  ]);

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error('useAuth must be used within an <AuthProvider>');
  return ctx;
}
